import * as Types from '@frameio/graphql-client/generated/types';

import { TypedDocumentNode as DocumentNode } from '@graphql-typed-document-node/core';
import gql from 'graphql-tag';
import { CompleteMembership } from '../../../../_global/fragments/generated/CompleteMembership';
import { ResourceMembership, ResourceGrantingAccess } from '../../generated/fragments';

import { useQuery, useLazyQuery, useMutation, useFragment, useSubscription } from '@apollo/client';
import type { QueryHookOptions, LazyQueryHookOptions, MutationHookOptions, SubscriptionHookOptions, UseFragmentOptions } from '@apollo/client';

export type GetDirectMembershipsQueryVariables = Types.Exact<{
  accountId: Types.Scalars['ID']['input'];
  locale?: Types.InputMaybe<Types.SupportedLocale>;
  page: Types.PageInput;
  resourceId: Types.Scalars['ID']['input'];
  searchTerm?: Types.InputMaybe<Types.Scalars['String']['input']>;
}>;


export type GetDirectMembershipsQuery = { __typename?: 'Query', indirectMemberships?: { __typename?: 'MembershipConnection', totalCount: number, nodes: Array<{ __typename?: 'Membership', id: string, type: string, accountUserGroup?: { __typename?: 'AccountUserGroup', id: string, name?: string | null, memberCount?: number | null, accountId: string, emoji?: string | null } | null, userGroup?: { __typename?: 'AdobeManagedUserGroup', syncStatus?: Types.UserGroupSyncStatus | null, id: string, name?: string | null, memberCount?: number | null } | { __typename?: 'FrameManagedUserGroup', id: string, name?: string | null, memberCount?: number | null } | { __typename?: 'WorkfrontManagedUserGroup', id: string, name?: string | null, memberCount?: number | null } | null, accountUser?: { __typename?: 'AccountUser', accountUserType?: Types.AccountUserTypeEnum | null, avatarColor?: Types.AvatarColor | null, email?: string | null, id: string, inviteEmailAddress?: string | null, isPending: boolean, name?: string | null, profileImageUrl?: string | null, userId?: string | null, licenseType?: Types.AccountUserLicenseTypeValue | null } | null, role?: { __typename?: 'Role', description?: string | null, id: string, name: string, order?: number | null } | null, grantingResource?: { __typename?: 'AccessGrantingResource', id: string, name?: string | null, resourceType: Types.ResourceTypeEnum } | null }> } | null, directMemberships?: { __typename?: 'MembershipConnection', totalCount: number, nodes: Array<{ __typename?: 'Membership', id: string, type: string, userGroup?: { __typename?: 'AdobeManagedUserGroup', syncStatus?: Types.UserGroupSyncStatus | null, id: string, name?: string | null, memberCount?: number | null, emoji?: string | null } | { __typename?: 'FrameManagedUserGroup', id: string, name?: string | null, memberCount?: number | null, emoji?: string | null } | { __typename?: 'WorkfrontManagedUserGroup', id: string, name?: string | null, memberCount?: number | null, emoji?: string | null } | null, accountUser?: { __typename?: 'AccountUser', accountUserType?: Types.AccountUserTypeEnum | null, avatarColor?: Types.AvatarColor | null, email?: string | null, id: string, inviteEmailAddress?: string | null, isPending: boolean, name?: string | null, profileImageUrl?: string | null, userId?: string | null, licenseType?: Types.AccountUserLicenseTypeValue | null, displayName?: string | null } | null, accountUserGroup?: { __typename?: 'AccountUserGroup', accountId: string, emoji?: string | null, id: string, memberCount?: number | null, name?: string | null } | null, role?: { __typename?: 'Role', description?: string | null, id: string, name: string, order?: number | null } | null, otherMemberships?: { __typename?: 'MembershipConnection', totalCount: number, nodes: Array<{ __typename?: 'Membership', id: string, accountUserGroup?: { __typename?: 'AccountUserGroup', accountId: string, emoji?: string | null, id: string, name?: string | null } | null, userGroup?: { __typename?: 'AdobeManagedUserGroup', emoji?: string | null, id: string, name?: string | null, account?: { __typename?: 'Account', id: string } | null } | { __typename?: 'FrameManagedUserGroup', emoji?: string | null, id: string, name?: string | null, account?: { __typename?: 'Account', id: string } | null } | { __typename?: 'WorkfrontManagedUserGroup', emoji?: string | null, id: string, name?: string | null, account?: { __typename?: 'Account', id: string } | null } | null, grantingResource?: { __typename?: 'AccessGrantingResource', id: string, name?: string | null, resourceType: Types.ResourceTypeEnum } | null }> } | null }>, pageInfo: { __typename?: 'PageInfo', endCursor?: string | null, hasNextPage: boolean } } | null, account?: { __typename?: 'Account', id: string, resource?: { __typename?: 'Account', uniqueMemberCount?: number | null, id: string } | { __typename?: 'AudioAsset', uniqueMemberCount?: number | null, id: string } | { __typename?: 'DocumentAsset', uniqueMemberCount?: number | null, id: string } | { __typename?: 'FileAsset', uniqueMemberCount?: number | null, id: string } | { __typename?: 'FolderAsset', uniqueMemberCount?: number | null, id: string } | { __typename?: 'ImageAsset', uniqueMemberCount?: number | null, id: string } | { __typename?: 'InteractiveAsset', uniqueMemberCount?: number | null, id: string } | { __typename?: 'InteractiveLiveAsset', uniqueMemberCount?: number | null, id: string } | { __typename?: 'ModelAsset', uniqueMemberCount?: number | null, id: string } | { __typename?: 'Project', uniqueMemberCount?: number | null, id: string } | { __typename?: 'UnsupportedAsset', uniqueMemberCount?: number | null, id: string } | { __typename?: 'VersionStackAsset', uniqueMemberCount?: number | null, id: string } | { __typename?: 'VideoAsset', uniqueMemberCount?: number | null, id: string } | { __typename?: 'Workspace', uniqueMemberCount?: number | null, id: string } | null } | null, unfilteredIndirectMembershipsTotalCount?: { __typename?: 'MembershipConnection', totalCount: number } | null, unfilteredDirectMembershipsTotalCount?: { __typename?: 'MembershipConnection', totalCount: number } | null };


export const GetDirectMemberships = /*#__PURE__*/ gql`
    query GetDirectMemberships($accountId: ID!, $locale: SupportedLocale, $page: PageInput!, $resourceId: ID!, $searchTerm: String) {
  indirectMemberships(
    resourceId: $resourceId
    page: {first: 50}
    filter: {query: $searchTerm}
  ) {
    nodes {
      ...CompleteMembership
      ...ResourceGrantingAccess
      accountUserGroup {
        id
        name
        memberCount
      }
      userGroup {
        id
        name
        memberCount
        ... on AdobeManagedUserGroup {
          syncStatus
        }
      }
    }
    totalCount
  }
  directMemberships(
    resourceId: $resourceId
    filter: {query: $searchTerm}
    page: $page
  ) {
    nodes {
      ...CompleteMembership
      ...ResourceMembership
      userGroup {
        id
        name
        memberCount
        ... on AdobeManagedUserGroup {
          syncStatus
        }
      }
    }
    pageInfo {
      endCursor
      hasNextPage
    }
    totalCount
  }
  account(by: {id: $accountId}) {
    id
    resource(id: $resourceId) {
      uniqueMemberCount
      id
    }
  }
  unfilteredIndirectMembershipsTotalCount: indirectMemberships(
    resourceId: $resourceId
    page: {first: 0}
  ) {
    totalCount
  }
  unfilteredDirectMembershipsTotalCount: directMemberships(
    resourceId: $resourceId
    page: {first: 0}
  ) {
    totalCount
  }
}
    ${CompleteMembership}
${ResourceGrantingAccess}
${ResourceMembership}` as unknown as DocumentNode<GetDirectMembershipsQuery, GetDirectMembershipsQueryVariables>;

          

          export const useGetDirectMembershipsQuery = (options?:QueryHookOptions<GetDirectMembershipsQuery, GetDirectMembershipsQueryVariables>) => useQuery(GetDirectMemberships, options);

          

          export type GetDirectMembershipsQueryOptions = QueryHookOptions<GetDirectMembershipsQuery, GetDirectMembershipsQueryVariables>;

          

          export const useGetDirectMembershipsLazyQuery = (options?:LazyQueryHookOptions<GetDirectMembershipsQuery, GetDirectMembershipsQueryVariables>) => useLazyQuery(GetDirectMemberships, options);

          

          export type GetDirectMembershipsQueryResult = ReturnType<typeof useGetDirectMembershipsQuery>;

          
